import { useCallback, useEffect, useState } from 'react';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErroCarregamento, { extrairMensagemErro } from '../../components/common/ErroCarregamento';
import StatusBadge from '../../components/common/StatusBadge';
import ConfirmarModal from '../../components/common/ConfirmarModal';
import usePaginacao from '../../hooks/usePaginacao';
import * as cadastrosApi from '../../api/cadastros';

/** Uma tela para os dois cadastros — cliente e sistema têm a mesma estrutura e as mesmas
 *  regras, e duplicar o componente só criaria duas cópias para manter em sincronia. */
export default function Cadastros({ tipo, titulo, subtitulo, placeholder }) {
  const [registros, setRegistros] = useState(null);
  const [erro, setErro] = useState('');
  const [novo, setNovo] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [editando, setEditando] = useState(null);
  const [nomeEditado, setNomeEditado] = useState('');
  const [confirmando, setConfirmando] = useState(null);

  const carregar = useCallback(async () => {
    setErro('');
    try {
      const { data } = await cadastrosApi.listar(tipo);
      setRegistros(data);
    } catch (err) {
      setErro(extrairMensagemErro(err, 'Não foi possível carregar os cadastros.'));
    }
  }, [tipo]);

  useEffect(() => {
    carregar();
    setNovo('');
    setEditando(null);
  }, [carregar]);

  const { paginados, Paginacao } = usePaginacao(registros || [], 15);

  if (erro && !registros) return <ErroCarregamento mensagem={erro} onRetry={carregar} />;
  if (!registros) return <LoadingSpinner />;

  const criar = async (e) => {
    e.preventDefault();
    setErro('');
    setSalvando(true);
    try {
      await cadastrosApi.criar(tipo, novo);
      setNovo('');
      await carregar();
    } catch (err) {
      setErro(extrairMensagemErro(err, 'Não foi possível cadastrar.'));
    } finally {
      setSalvando(false);
    }
  };

  const salvarNome = async (registro) => {
    setErro('');
    try {
      await cadastrosApi.atualizar(tipo, registro.id, { nome: nomeEditado });
      setEditando(null);
      await carregar();
    } catch (err) {
      setErro(extrairMensagemErro(err, 'Não foi possível renomear.'));
    }
  };

  const alternarAtivo = async (registro) => {
    setErro('');
    try {
      await cadastrosApi.atualizar(tipo, registro.id, { ativo: !registro.ativo });
      await carregar();
    } catch (err) {
      setErro(extrairMensagemErro(err, 'Não foi possível alterar a situação.'));
    }
  };

  return (
    <div>
      <PageHeader titulo={titulo} subtitulo={subtitulo} />

      <div className="card-soft mb-3">
        <div className="card-soft__body">
          <form onSubmit={criar} className="d-flex gap-2 align-items-end flex-wrap">
            <div style={{ flex: 1, minWidth: 240 }}>
              <label className="wh-label">Novo cadastro</label>
              <input
                className="wh-input"
                value={novo}
                onChange={(e) => setNovo(e.target.value)}
                placeholder={placeholder}
              />
            </div>
            <button type="submit" className="wh-btn wh-btn-primary" disabled={salvando || !novo.trim()}>
              <i className="bi bi-plus-lg" /> Cadastrar
            </button>
          </form>
          {erro && (
            <div className="alert alert-danger py-2 px-3 mt-3 mb-0" style={{ fontSize: 13 }}>
              {erro}
            </div>
          )}
        </div>
      </div>

      <div className="card-soft">
        {registros.length === 0 ? (
          <div className="empty-state">
            <i className="bi bi-collection" />
            <div style={{ fontWeight: 600, color: 'var(--wh-ink)' }}>Nenhum cadastro ainda.</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>
              Você também pode cadastrar direto no formulário do endpoint — basta digitar um nome novo.
            </div>
          </div>
        ) : (
          <div className="wh-table-wrap">
            <table className="wh-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th style={{ textAlign: 'right' }}>Endpoints</th>
                  <th>Situação</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {paginados.map((r) => (
                  <tr key={r.id}>
                    <td style={{ fontWeight: 600 }}>
                      {editando === r.id ? (
                        <input
                          className="wh-input"
                          value={nomeEditado}
                          autoFocus
                          onChange={(e) => setNomeEditado(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') salvarNome(r);
                            if (e.key === 'Escape') setEditando(null);
                          }}
                          style={{ maxWidth: 280 }}
                        />
                      ) : (
                        r.nome
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>{r.total_endpoints}</td>
                    <td>
                      <StatusBadge status={r.ativo ? 'ativo' : 'inativo'} />
                    </td>
                    <td>
                      <div className="d-flex gap-1 justify-content-end">
                        {editando === r.id ? (
                          <>
                            <button type="button" className="wh-btn wh-btn-primary wh-btn-sm" onClick={() => salvarNome(r)}>
                              Salvar
                            </button>
                            <button type="button" className="wh-btn wh-btn-outline wh-btn-sm" onClick={() => setEditando(null)}>
                              Cancelar
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              className="wh-btn wh-btn-outline wh-btn-sm"
                              title="Renomear — vale para todos os endpoints vinculados"
                              onClick={() => {
                                setEditando(r.id);
                                setNomeEditado(r.nome);
                              }}
                            >
                              <i className="bi bi-pencil" />
                            </button>
                            <button
                              type="button"
                              className="wh-btn wh-btn-outline wh-btn-sm"
                              title={r.ativo ? 'Desativar (some das sugestões, mantém o histórico)' : 'Reativar'}
                              onClick={() => alternarAtivo(r)}
                            >
                              <i className={`bi bi-${r.ativo ? 'pause' : 'play'}-circle`} />
                            </button>
                            <button
                              type="button"
                              className="wh-btn wh-btn-danger wh-btn-sm"
                              title={
                                r.total_endpoints
                                  ? 'Em uso por endpoints — desative em vez de excluir'
                                  : 'Excluir'
                              }
                              disabled={r.total_endpoints > 0}
                              onClick={() =>
                                setConfirmando({
                                  titulo: `Excluir ${tipo}`,
                                  mensagem: `"${r.nome}" será removido do cadastro. Não há endpoint vinculado, então nada mais é afetado.`,
                                  acao: () => cadastrosApi.excluir(tipo, r.id).then(carregar),
                                })
                              }
                            >
                              <i className="bi bi-trash" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Paginacao />

      {confirmando && (
        <ConfirmarModal
          titulo={confirmando.titulo}
          mensagem={confirmando.mensagem}
          textoBotao="Excluir"
          onConfirmar={confirmando.acao}
          onClose={() => setConfirmando(null)}
        />
      )}
    </div>
  );
}
